import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.container = this.element || document.body
    this.loading = false
    this.requestedPages = new Set()
    this.BOTTOM_OFFSET = 10 // px from bottom counts as "near bottom"

    this._ensureSentinel()

    this.observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && this._maybeLoad(),
      { root: null, rootMargin: "800px 0px 800px 0px", threshold: 0 }
    )
    this.observer.observe(this.sentinel)

    // Kickstart if content is initially shorter than the viewport
    this._kickstart()
  }

  disconnect() {
    this.observer?.disconnect()
  }

  // --- Core logic -----------------------------------------------------------

  _maybeLoad() {
    if (this.loading) return

    const { page, nextPage, lastPage } = this._pageInfo()
    if (!nextPage) return
    if (page >= lastPage) return
    if (this.requestedPages.has(nextPage)) return // hard de-dupe

    this._load(nextPage)
  }

  _load(nextPage) {
    this.loading = true
    this.requestedPages.add(nextPage)

    const url = new URL(window.location.href)
    url.searchParams.set("page", nextPage)

    fetch(url, { headers: { "Accept": "text/vnd.turbo-stream.html" } })
      .then(r => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(html => {
        if (window.Turbo?.renderStreamMessage) {
          Turbo.renderStreamMessage(html)
        } else {
          this.container.insertAdjacentHTML("beforeend", html)
        }

        // Keep sentinel last; IO needs it at the end
        this._ensureSentinel(true)

        // Force a transition so IO will fire again even if sentinel never left view
        this._forceReobserve()

        // If we’re still near the bottom (very tall viewport or user is pinned),
        // proactively try again (no-ops if no more pages).
        if (this._isNearBottom()) this._maybeLoad()
      })
      .catch(err => {
        // Allow retry of this page on failure
        this.requestedPages.delete(nextPage)
        console.error("Error fetching more content:", err)
      })
      .finally(() => {
        this.loading = false
      })
  }

  // --- Helpers --------------------------------------------------------------

  _pageInfo() {
    const scrolls = this.container.querySelectorAll(".scroll")
    const ds = scrolls[scrolls.length - 1]?.dataset || {}
    return {
      page: parseInt(ds.page || "0", 10),
      nextPage: ds.nextPage,
      lastPage: parseInt(ds.lastPage || "0", 10),
    }
  }

  _ensureSentinel(moveToEnd = false) {
    this.sentinel = this.container.querySelector('[data-role="infinite-sentinel"]')
    if (!this.sentinel) {
      this.sentinel = document.createElement("div")
      this.sentinel.dataset.role = "infinite-sentinel"
      Object.assign(this.sentinel.style, { minHeight: "1px", width: "100%" })
      this.container.appendChild(this.sentinel)
    } else if (moveToEnd) {
      this.container.appendChild(this.sentinel)
    }
  }

  _forceReobserve() {
    // Some browsers won’t callback again if the element never left intersecting state.
    // Unobserve/observe forces a fresh transition.
    this.observer.unobserve(this.sentinel)
    // Next frame guarantees layout has settled
    requestAnimationFrame(() => this.observer.observe(this.sentinel))
  }

  _isNearBottom() {
    const { scrollTop, clientHeight, scrollHeight } = document.documentElement
    return scrollTop + clientHeight >= scrollHeight - this.BOTTOM_OFFSET
  }

  _kickstart() {
    // If the page is short or the user is already at bottom, start loading immediately.
    if (this._isNearBottom()) this._maybeLoad()
  }
}

